import {
    Typography,
    DialogContent,
    Button,
    Grid,
    Paper,
    TableBody,
    Table,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    useTheme,
} from "@mui/material";
import React, { useState } from "react";
import AssessmentClient, { AssessmentErrors, AssessmentGraph, AssessmentProficiencyDisplay } from "../apiClients/AssessmentClient";
import { useErrorDisplay } from "../components/ErrorDisplay";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import TableHeaderCell from "../components/TableHeaderCell";
import { useWindowDimensions } from "../components/WindowDimensions";
import { ExtractForSetError } from "../Helpers";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import useViewportMode from "../hooks/useViewportMode";

export interface AssessmentDisplayProps extends Omit<IconButtonExProps, 'onClick'> {
    assessmentId?: string;
    icon?: JSX.Element;
}

const AssessmentDisplayPopup = ({ assessmentId, icon = <RecentActorsIcon htmlColor='green' fontSize='small' />, ...btnProps }: AssessmentDisplayProps) => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const { height } = useWindowDimensions();
    const viewport = useViewportMode();

    const [assessment, setAssessment] = useState<AssessmentGraph>(new AssessmentGraph());
    const [scoreColor, setScoreColor] = useState<string>();

    const [expanded, setExpanded] = useState<string | false>(false);

    const title = `Assessment`;

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const loadData = async () => {
        try {
            setBusy(true);

             const apiResponse = await new AssessmentClient().GetSingle(assessmentId);
             if(ExtractForSetError(apiResponse, AssessmentErrors.GetSingle, setError))
             return;

             const a = apiResponse.Content;
            setAssessment(a);

            if (a.OverallScore > "7") {
                setScoreColor("success");
            }
            else if (a.OverallScore > "4") {
                setScoreColor("secondary");
            }
            else {
                setScoreColor("error");
            }

        } catch (error) {
            setError(error, AssessmentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    }

    const renderProficiencyCard = (proficiency: AssessmentProficiencyDisplay) => (
        <MobileEntityCard
            ariaLabel={`Assessment proficiency: ${proficiency.ProficiencyName}`}
            title={proficiency.ProficiencyName}
            subtitle={proficiency.ProficiencyType}
            fields={[{ label: 'Selection', value: proficiency.Selection, fullWidth: true }]}
        />
    );

    return (
        <>
            <ErrorDisplay />
            <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

            <ResponsiveDialog aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="xl" tabletFullScreen="portrait">
                <ResponsiveDialogHeader id="customized-dialog-title" title={title} onClose={() => setOpen(false)} />
                <DialogContent dividers sx={{ p: viewport.isPhone ? 1 : theme.spacing(2), minHeight: 0, overflowY: 'auto' }}>
                    <div style={{ height: viewport.isDesktop ? height - 300 : 'auto', width: '100%' }}>
                        <BusySpinner busy={busy}>
                            <Paper sx={{ padding: theme.spacing(1)  }} elevation={5}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Paper elevation={3} color={scoreColor} sx={{ padding: theme.spacing(1)  }}>
                                            <Typography variant="h5" >{`${assessment.FirstName} ${assessment.LastName}`}</Typography>
                                            <Typography variant="h6" >{`Overall Score: ${assessment.OverallScore}`}</Typography>
                                            <Typography variant="subtitle1">{`assessed by: ${assessment.AssessorName} on ${assessment.CreatedDate}`}</Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12}>
                                        {
                                            assessment.GroupScores?.map((gs) => (
                                                <Accordion key={gs.GroupName} expanded={expanded === gs.GroupName} onChange={handleChange(gs.GroupName)}  >
                                                    <AccordionSummary
                                                        expandIcon={<ExpandMoreIcon />}
                                                        aria-controls={`panel-${gs.GroupName}-content`}
                                                        id={`panel-${gs.GroupName}-header`}
                                                    >
                                                        <Typography >{gs.GroupName}: {gs.Score}</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <ResponsiveCollection
                                                            viewMode={viewport.mode}
                                                            items={gs.Proficiencies.filter(f => f.GroupName === gs.GroupName)}
                                                            getKey={proficiency => proficiency.ProficiencyName}
                                                            renderCard={renderProficiencyCard}
                                                            desktop={
                                                                <TableContainer sx={{ border: 1 }} style={{ borderRadius: '5px' }} >
                                                                    <Table size="small" aria-label="a dense table">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableHeaderCell style={{ width: 20 }}>Proficiency</TableHeaderCell>
                                                                                <TableHeaderCell style={{ width: 20 }}>Selection</TableHeaderCell>
                                                                                <TableHeaderCell style={{ width: 20 }}>Type</TableHeaderCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {gs.Proficiencies
                                                                                .filter(f => f.GroupName === gs.GroupName)
                                                                                .map((p) => (
                                                                                    <TableRow key={p.ProficiencyName}>
                                                                                        <TableCell>{p.ProficiencyName}</TableCell>
                                                                                        <TableCell>
                                                                                            <Typography>{p.Selection}</Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography>{p.ProficiencyType}</Typography>
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            }
                                                            ariaLabel={`${gs.GroupName} proficiencies`}
                                                        />
                                                    </AccordionDetails>
                                                </Accordion>
                                            ))
                                        }
                                    </Grid>
                                </Grid>
                            </Paper>
                        </BusySpinner>
                    </div>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: 1 }}>
                    <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Close</Button>
                </StickyDialogActions>
            </ResponsiveDialog>
        </>
    );
}

export default AssessmentDisplayPopup;
